import React, { useState, useEffect, useMemo } from "react";
import { Save, X } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { mapUIParamsToBackend, buildSOPDataFromRecipe, parseStoredComments } from "../data/sopDataByFormat";
import { useAuthStore } from "@/store/useAuthStore";
import { cn } from "@/lib/utils";

const defaultParams = [
  { label: "Dough Temperature", value: "", unit: "°C" },
  { label: "SG", value: "", unit: "" },
  { label: "pH", value: "", unit: "" },
];

const confectioneryDefaultParams = [
  { label: "Cooking pH", value: "", unit: "" },
  { label: "Final pH", value: "", unit: "" },
  { label: "Brix", value: "", unit: "" },
  { label: "Cooking Temperature", value: "", unit: "°C" },
  { label: "Depositing Temperature", value: "", unit: "°C" },
  { label: "Cooking Time", value: "", unit: "min" },
  { label: "Gel Forming Time", value: "", unit: "min" },
  { label: "1st Dry Time", value: "", unit: "" },
  { label: "2nd Dry Time", value: "", unit: "" },
];

const beverageDefaultParams = [
  { label: "Homogenization Pressure", value: "", unit: "bar" },
  { label: "Pasteurization Temperature", value: "", unit: "" },
  { label: "Pasteurization Time", value: "", unit: "" },
  { label: "Aeration", value: "", unit: "" },
  { label: "Viscosity", value: "", unit: "" },
  { label: "pH", value: "", unit: "" },
  { label: "Brix", value: "", unit: "" },
  { label: "Acidity", value: "", unit: "" },
  { label: "Salt", value: "", unit: "%" },
  { label: "Filling Temperature", value: "", unit: "°C" },
  { label: "CO2 Filling Temperature", value: "", unit: "°C" },
  { label: "CO2 Pressure", value: "", unit: "" },
];

const beveragePsdDefaultParams = [
  { label: "Moisture", value: "", unit: "" },
  { label: "pH", value: "", unit: "" },
  { label: "Brix", value: "", unit: "" },
  { label: "Acidity", value: "", unit: "" },
  { label: "Salt", value: "", unit: "" },
];

const defaultTunnelZones = [
  { label: "Top Temperature", zone1: "", zone2: "", zone3: "", unit: "°C" },
  { label: "Bottom Temperature", zone1: "", zone2: "", zone3: "", unit: "°C" },
];

const defaultRotaryBakings = [
  { label: "Rotary Temperature", baking1: "", baking2: "", unit: "°C" },
  { label: "Oven Time", baking1: "", baking2: "", unit: "min" },
  { label: "Steam", baking1: "", baking2: "", unit: "unit" },
];

const defaultAfterBake = [
  { label: "Weight", value: "", unit: "g" },
  { label: "Size", value: "", unit: "" },
  { label: "Aeration", value: "", unit: "" },
  { label: "aW", value: "", unit: "" },
  { label: "Moisture", value: "", unit: "" },
];

// ================= STANDARD OPERATING PROCEDURE VERSION COLUMN =================
export default function StandardOperatingProcedure({
  vItem,
  data,
  normalizedVItem,
  recipeFormat = "bakery",
  isFirstVersion = false,
  sopRef,
  minHeight,
  onSaveSpecificFields,
  isConfectionary = false,
  formatDate = (d) => d || "-",
  vIsFinalized = false,
  isSelectingForCompare = false,
  hideOthers = false,
  isSamplePage = false,
  isFromFinalRecipe = false,
}) {
  const [isSOPEditing, setIsSOPEditing] = useState(false);
  const [isSavingSOP, setIsSavingSOP] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [localActivities, setLocalActivities] = useState([]);

  const { user: currentUser } = useAuthStore();
  const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || "User";

  const isApproved =
    vIsFinalized ||
    (!isSamplePage &&
      ["final", "approved", "finalized"].includes(
        String(
          vItem?.recipeStatus ||
            vItem?.status ||
            normalizedVItem?.recipeStatus ||
            normalizedVItem?.status ||
            data?.recipeStatus ||
            data?.status ||
            ""
        ).toLowerCase()
      ));

  const formatCandidates = [
    recipeFormat,
    data?.recipeType,
    data?.applicationLab?.category?.name,
    normalizedVItem?.recipeType,
    normalizedVItem?.applicationLab?.category?.name,
    vItem?.recipeType,
    vItem?.applicationLab?.category?.name,
  ];

  const checkIsBeveragePsd = (val) => {
    if (!val) return false;
    const str = String(val).trim().toLowerCase();
    return (
      str === "beverage psd" ||
      str === "beverage-psd" ||
      str === "beveragepsd" ||
      str.includes("psd")
    );
  };

  const isBeveragePsd = formatCandidates.some(checkIsBeveragePsd);

  const checkIsBeverage = (val) => {
    if (!val) return false;
    const str = String(val).trim().toLowerCase();
    return !checkIsBeveragePsd(str) && (str === "beverage" || str.startsWith("beverage"));
  };

  const isBeverage = !isBeveragePsd && formatCandidates.some(checkIsBeverage);

  const isConfection =
    isConfectionary ||
    formatCandidates.some((f) => String(f || "").toLowerCase().includes("confection"));

  // For Beverage and Beverage PSD formats, baking-related SOP sections
  // (Oven Temperature, Tunnel Baking, Rotary Baking, After Bake, Analytical Report)
  // are not applicable and must not be displayed.
  // For Confectionery, baking sections are also not displayed.
  // For all other formats (Bakery), keep existing SOP behavior unchanged.
  const shouldShowBakeSections = !isConfection && !isBeverage && !isBeveragePsd;
  const isBakery = shouldShowBakeSections;

  const effectiveIsFromFinalRecipe =
    isFromFinalRecipe ||
    ["final", "approved", "finalized"].includes(
      String(
        vItem?.recipeStatus ||
          vItem?.status ||
          normalizedVItem?.recipeStatus ||
          normalizedVItem?.status ||
          data?.recipeStatus ||
          data?.status ||
          ""
      ).toLowerCase()
    );

  // Editing permissions:
  // On Add Sample page (samples from Final Recipes or In-Development):
  // - Procedure is never editable on sample pages (regardless of format or final/in-dev status)
  // - Beverage, Beverage PSD, Confectionery: All editable except Procedure
  // - Bakery: ONLY After-Bake Analysis editable if from final recipe
  // On In-Development recipes (non-sample page): all sections editable
  const canEditProcedure = isSamplePage ? false : true;
  const canEditParameters = isSamplePage && effectiveIsFromFinalRecipe ? !isBakery : true;
  const canEditOven = isSamplePage && effectiveIsFromFinalRecipe ? false : true;
  const canEditAfterBake = true;

  const effectiveDefaultParams = useMemo(() => {
    if (isConfection) return confectioneryDefaultParams;
    if (isBeveragePsd) return beveragePsdDefaultParams;
    if (isBeverage) return beverageDefaultParams;
    return defaultParams;
  }, [isConfection, isBeveragePsd, isBeverage]);

  const getCleanProcedureParams = (sop) => {
    const params =
      sop?.procedureParameters?.analyticalReport?.length > 0
        ? sop.procedureParameters.analyticalReport
        : effectiveDefaultParams;

    if (isBeveragePsd) {
      return params.filter(
        (p) => !(p.label || p.parameterName || "").toLowerCase().includes("pasteuriz")
      );
    }
    return params;
  };

  const initialSOP = useMemo(
    () => buildSOPDataFromRecipe(normalizedVItem, recipeFormat),
    [normalizedVItem, recipeFormat]
  );

  const [draftProcedure, setDraftProcedure] = useState(
    initialSOP?.procedure?.raw ||
    (initialSOP?.procedure?.steps || []).join("\n") ||
    ""
  );

  const [draftParams, setDraftParams] = useState(() => getCleanProcedureParams(initialSOP));

  const [draftTunnelZones, setDraftTunnelZones] = useState(
    initialSOP?.ovenTemperature?.tunnelBaking?.zones?.length > 0
      ? initialSOP.ovenTemperature.tunnelBaking.zones
      : defaultTunnelZones
  );
  const [draftBakingTime, setDraftBakingTime] = useState(
    initialSOP?.ovenTemperature?.tunnelBaking?.bakingTime?.value || ""
  );
  const [draftBeltSpeed, setDraftBeltSpeed] = useState(
    initialSOP?.ovenTemperature?.tunnelBaking?.beltSpeed?.value || ""
  );

  const [draftRotaryBakings, setDraftRotaryBakings] = useState(
    initialSOP?.ovenTemperature?.normalBaking?.bakings?.length > 0
      ? initialSOP.ovenTemperature.normalBaking.bakings
      : defaultRotaryBakings
  );

  const [draftAfterBake, setDraftAfterBake] = useState(
    initialSOP?.afterBake?.analyticalReport?.length > 0
      ? initialSOP.afterBake.analyticalReport
      : defaultAfterBake
  );

  useEffect(() => {
    const updatedSop = buildSOPDataFromRecipe(normalizedVItem, recipeFormat);
    setDraftProcedure(
      updatedSop?.procedure?.raw ||
      (updatedSop?.procedure?.steps || []).join("\n") ||
      ""
    );
    setDraftParams(getCleanProcedureParams(updatedSop));
    setDraftTunnelZones(
      updatedSop?.ovenTemperature?.tunnelBaking?.zones?.length > 0
        ? updatedSop.ovenTemperature.tunnelBaking.zones
        : defaultTunnelZones
    );
    setDraftBakingTime(updatedSop?.ovenTemperature?.tunnelBaking?.bakingTime?.value || "");
    setDraftBeltSpeed(updatedSop?.ovenTemperature?.tunnelBaking?.beltSpeed?.value || "");
    setDraftRotaryBakings(
      updatedSop?.ovenTemperature?.normalBaking?.bakings?.length > 0
        ? updatedSop.ovenTemperature.normalBaking.bakings
        : defaultRotaryBakings
    );
    setDraftAfterBake(
      updatedSop?.afterBake?.analyticalReport?.length > 0
        ? updatedSop.afterBake.analyticalReport
        : defaultAfterBake
    );
  }, [normalizedVItem, recipeFormat, isConfection, isBeverage, isBeveragePsd, effectiveDefaultParams]);

  // Save Standard Operating for THIS version
  const handleSaveSOP = async () => {
    try {
      setIsSavingSOP(true);
      const topZone = draftTunnelZones.find((z) => z.label === "Top Temperature");
      const bottomZone = draftTunnelZones.find((z) => z.label === "Bottom Temperature");
      const tunnelData = {
        topTemperature: [topZone?.zone1, topZone?.zone2, topZone?.zone3].map(Number).filter(Number.isFinite),
        bottomTemperature: [bottomZone?.zone1, bottomZone?.zone2, bottomZone?.zone3].map(Number).filter(Number.isFinite),
        bakingTime: Number(draftBakingTime) || undefined,
        beltSpeed: Number(draftBeltSpeed) || undefined,
      };

      const ovenTempBaking = draftRotaryBakings.find((b) => b.label === "Rotary Temperature" || b.label === "Oven Temperature");
      const ovenTimeBaking = draftRotaryBakings.find((b) => b.label === "Oven Time");
      const steamBaking = draftRotaryBakings.find((b) => b.label === "Steam");
      const normalData = {
        ovenTemperature: [ovenTempBaking?.baking1, ovenTempBaking?.baking2].map(Number).filter(Number.isFinite),
        ovenTime: [ovenTimeBaking?.baking1, ovenTimeBaking?.baking2].map(Number).filter(Number.isFinite),
        steam: Number(steamBaking?.baking1) || undefined,
        steam2: Number(steamBaking?.baking2) || undefined,
      };

      const findVal = (label) => {
        const item = draftAfterBake.find(
          (r) => r.label === label || r.label?.toLowerCase() === label.toLowerCase()
        );
        return item?.value ? Number(item.value) : undefined;
      };
      const abData = {
        weight: findVal("Weight"),
        size: findVal("Size"),
        aeration: findVal("Aeration"),
        aW: findVal("aW"),
        moisture: findVal("Moisture"),
      };

      let updatedProcedureOthers;
      if (newCommentText.trim()) {
        const newEntry = {
          userName: currentUserName,
          user: currentUserName,
          name: currentUserName,
          text: newCommentText.trim(),
          comment: newCommentText.trim(),
          createdAt: new Date().toISOString(),
        };
        const updatedList = [...effectiveActivities, newEntry];
        updatedProcedureOthers = JSON.stringify(updatedList);
        setLocalActivities((prev) => [...prev, newEntry]);
        setNewCommentText("");
      }

      if (onSaveSpecificFields) {
        const paramsToSave = isBeveragePsd
          ? draftParams.filter(
              (p) => !(p.label || p.parameterName || "").toLowerCase().includes("pasteuriz")
            )
          : draftParams;

        const payload = {
          ...(canEditProcedure ? { procedureSOP: draftProcedure } : {}),
          ...(canEditParameters ? { procedureParameters: mapUIParamsToBackend(paramsToSave) } : {}),
          ...(shouldShowBakeSections
            ? {
              ...(canEditOven ? { ovenTemperatureTunnelBaking: tunnelData, normalBaking: normalData } : {}),
              ...(canEditAfterBake ? { afterBake: abData } : {}),
            }
            : {}),
          ...(updatedProcedureOthers ? { procedureOthers: updatedProcedureOthers } : {}),
        };
        await onSaveSpecificFields(payload, vItem?._id || data?._id);
      }

      setIsSOPEditing(false);
    } catch (err) {
      console.error("Failed to save Standard Operating for version:", err);
    } finally {
      setIsSavingSOP(false);
    }
  };

  const dynamicAuthor =
    vItem?.createdBy?.name ||
    vItem?.createdBy?.fullName ||
    (typeof vItem?.createdBy === "string" && vItem.createdBy.length < 30 ? vItem.createdBy : null) ||
    data?.createdBy?.name ||
    data?.createdBy?.fullName ||
    data?.independentRecipeRaisedBy ||
    data?.raisedBy ||
    currentUserName;

  // Stored activities from database (vItem.activities + parsed vItem.procedureOthers)
  const storedActivities = useMemo(() => {
    const list = [];
    if (vItem) {
      if (Array.isArray(vItem.activities) && vItem.activities.length > 0) {
        list.push(...vItem.activities);
      }
    } else if (Array.isArray(data?.activities) && data.activities.length > 0) {
      list.push(...data.activities);
    }

    const procOthers = vItem
      ? (vItem.procedureOthers || normalizedVItem?.procedureOthers)
      : data?.procedureOthers;

    if (procOthers && (typeof procOthers === "string" || Array.isArray(procOthers))) {
      const parsed = parseStoredComments(
        procOthers,
        dynamicAuthor,
        vItem?.updatedAt || vItem?.createdAt || data?.updatedAt || data?.createdAt
      );

      parsed.forEach((p) => {
        const textKey = (p.text || p.comment || "").trim();
        const dateKey = p.createdAt ? new Date(p.createdAt).getTime() : 0;
        const exists = list.some((existing) => {
          const eText = (existing.text || existing.comment || "").trim();
          const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
          return eText === textKey && (Math.abs(eDate - dateKey) < 1000 || !eDate || !dateKey);
        });
        if (!exists) {
          list.push(p);
        }
      });
    }

    return list;
  }, [vItem, normalizedVItem, data, dynamicAuthor]);

  // Consolidate stored activities and newly posted local comments
  const effectiveActivities = useMemo(() => {
    const list = [...storedActivities];
    localActivities.forEach((local) => {
      const lText = (local.text || local.comment || "").trim();
      const lDate = local.createdAt ? new Date(local.createdAt).getTime() : 0;
      const exists = list.some((existing) => {
        const eText = (existing.text || existing.comment || "").trim();
        const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
        return eText === lText && (Math.abs(eDate - lDate) < 1000 || !eDate || !lDate);
      });
      if (!exists) {
        list.push(local);
      }
    });
    return list;
  }, [storedActivities, localActivities]);

  // Recipe-specific comment submission handler supporting multiple separate entries
  const handleSendComment = async () => {
    const text = newCommentText.trim();
    if (!text) return;

    try {
      const targetId = vItem?._id || data?._id;
      const newEntry = {
        userName: currentUserName,
        user: currentUserName,
        name: currentUserName,
        text,
        comment: text,
        createdAt: new Date().toISOString(),
      };

      // Append new entry to the full list of existing comments
      const updatedList = [...effectiveActivities, newEntry];

      setLocalActivities((prev) => [...prev, newEntry]);
      setNewCommentText("");

      if (onSaveSpecificFields) {
        await onSaveSpecificFields(
          {
            procedureOthers: JSON.stringify(updatedList),
          },
          targetId
        );
      }
    } catch (err) {
      console.error("Failed to save recipe comment:", err);
    }
  };

  const handleCancelSOP = () => {
    const updatedSop = buildSOPDataFromRecipe(normalizedVItem, recipeFormat);
    setDraftProcedure(
      updatedSop?.procedure?.raw || (updatedSop?.procedure?.steps || []).join("\n") || ""
    );
    setDraftParams(
      updatedSop?.procedureParameters?.analyticalReport?.length > 0
        ? updatedSop.procedureParameters.analyticalReport
        : effectiveDefaultParams
    );
    setDraftTunnelZones(
      updatedSop?.ovenTemperature?.tunnelBaking?.zones?.length > 0
        ? updatedSop.ovenTemperature.tunnelBaking.zones
        : defaultTunnelZones
    );
    setDraftBakingTime(updatedSop?.ovenTemperature?.tunnelBaking?.bakingTime?.value || "");
    setDraftBeltSpeed(updatedSop?.ovenTemperature?.tunnelBaking?.beltSpeed?.value || "");
    setDraftRotaryBakings(
      updatedSop?.ovenTemperature?.normalBaking?.bakings?.length > 0
        ? updatedSop.ovenTemperature.normalBaking.bakings
        : defaultRotaryBakings
    );
    setDraftAfterBake(
      updatedSop?.afterBake?.analyticalReport?.length > 0
        ? updatedSop.afterBake.analyticalReport
        : defaultAfterBake
    );
    setIsSOPEditing(false);
  };

  const topZone = draftTunnelZones.find((z) => z.label === "Top Temperature") || { zone1: "", zone2: "", zone3: "" };
  const bottomZone = draftTunnelZones.find((z) => z.label === "Bottom Temperature") || { zone1: "", zone2: "", zone3: "" };
  const ovenTempRow = draftRotaryBakings.find((b) => b.label === "Rotary Temperature" || b.label === "Oven Temperature") || { baking1: "", baking2: "" };
  const ovenTimeRow = draftRotaryBakings.find((b) => b.label === "Oven Time") || { baking1: "", baking2: "" };
  const steamRow = draftRotaryBakings.find((b) => b.label === "Steam") || { baking1: "", baking2: "" };

  const setContentRef = React.useCallback(
    (node) => {
      if (!sopRef) return;
      if (typeof sopRef === "function") {
        sopRef(node);
      } else if (sopRef && "current" in sopRef) {
        sopRef.current = node;
      }
    },
    [sopRef]
  );

  return (
    <div
      ref={setContentRef}
      style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
      className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 space-y-6 lg:space-y-3 xl:space-y-4 2xl:space-y-5 3xl:space-y-6 border-b border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#0D0B14] flex flex-col justify-between"
    >
      <div className="space-y-6 lg:space-y-3 xl:space-y-4 2xl:space-y-5 3xl:space-y-6">
        {/* Procedure Block (Image 2) */}
        <div>
          <div className="flex items-center justify-between mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-900 dark:text-white leading-tight">
                Procedure
              </span>
              {isSOPEditing && !canEditProcedure && (
                <span className="text-[10px] lg:text-[7px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                  Read-only
                </span>
              )}
            </div>

            {isSOPEditing ? (
              <div className="flex items-center overflow-hidden rounded-xl lg:rounded-md bg-[#4B208B] text-white shadow-sm">
                <button
                  type="button"
                  onClick={handleSaveSOP}
                  disabled={isSavingSOP}
                  title="Save Standard Operating"
                  className="flex items-center justify-center w-6.5 lg:w-3.5 xl:w-4.5 2xl:w-5.5 3xl:w-6.5 h-6.5 lg:h-3.5 xl:h-4.5 2xl:h-5.5 3xl:h-6.5 hover:bg-[#3E1B77] transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <Save className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                </button>
                <div className="w-px h-4 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-4 bg-white/30" />
                <button
                  type="button"
                  onClick={handleCancelSOP}
                  disabled={isSavingSOP}
                  title="Cancel Standard Operating"
                  className="flex items-center justify-center w-6.5 lg:w-3.5 xl:w-4.5 2xl:w-5.5 3xl:w-6.5 h-6.5 lg:h-3.5 xl:h-4.5 2xl:h-5.5 3xl:h-6.5 hover:bg-[#3E1B77] transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <X className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                </button>
              </div>
            ) : (!isSelectingForCompare && (isSamplePage || !isApproved)) ? (
              <button
                type="button"
                onClick={() => setIsSOPEditing(true)}
                title="Edit Standard Operating"
                className="w-6.5 lg:w-3.5 xl:w-4.5 2xl:w-5.5 3xl:w-6.5 h-6.5 lg:h-3.5 xl:h-4.5 2xl:h-5.5 3xl:h-6.5 rounded-full bg-[#4B208B] hover:bg-[#3E1B77] text-white flex items-center justify-center shadow-sm cursor-pointer"
              >
                <svg className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.34847 0.928711H1.48022C1.18709 0.928711 0.905978 1.04516 0.69871 1.25244C0.491442 1.45972 0.375 1.74086 0.375 2.03399V9.77098C0.375 10.0641 0.491442 10.3453 0.69871 10.5525C0.905978 10.7598 1.18709 10.8763 1.48022 10.8763H9.21673C9.50985 10.8763 9.79096 10.7598 9.99823 10.5525C10.2055 10.3453 10.3219 10.0641 10.3219 9.77098V5.90249" stroke="white" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8.87126 0.718368C9.0911 0.498513 9.38927 0.375 9.70017 0.375C10.0111 0.375 10.3092 0.498513 10.5291 0.718368C10.7489 0.938222 10.8724 1.23641 10.8724 1.54733C10.8724 1.85825 10.7489 2.15644 10.5291 2.37629L5.54843 7.35781C5.41721 7.48892 5.25511 7.58489 5.07705 7.63689L3.48941 8.10111C3.44186 8.11498 3.39146 8.11581 3.34347 8.10352C3.29549 8.09122 3.25169 8.06626 3.21667 8.03123C3.18165 7.9962 3.15668 7.95241 3.14439 7.90442C3.13209 7.85643 3.13293 7.80603 3.14679 7.75847L3.61098 6.17073C3.66322 5.99281 3.75938 5.83089 3.8906 5.69988L8.87126 0.718368Z" stroke="white" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}
          </div>

          {isSOPEditing && canEditProcedure ? (
            <textarea
              rows={4}
              value={draftProcedure}
              onChange={(e) => setDraftProcedure(e.target.value)}
              placeholder="Enter procedure steps..."
              className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] bg-white dark:bg-[#151221] border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200 outline-none resize-y"
            />
          ) : (
            <div className="p-3.5 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-3.5 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] bg-[#F9FAFB] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed text-gray-800 dark:text-gray-200">
              {draftProcedure || "-"}
            </div>
          )}
        </div>

        {/* Parameters Block (Image 2) */}
        <div>
          <div className="flex items-center gap-2 mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
            <span className="font-bold text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-900 dark:text-white">
              Parameters
            </span>
            {isSOPEditing && !canEditParameters && (
              <span className="text-[10px] lg:text-[7px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                Read-only
              </span>
            )}
          </div>

          <div className="border border-[#EEEBF4] dark:border-primary/30 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] overflow-hidden divide-y divide-[#EEEBF4] dark:divide-primary/30 bg-[#F9FAFB] dark:bg-[#121019] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
            {draftParams.map((param, pIdx) => (
              <div key={pIdx} className="flex items-center divide-x divide-[#EEEBF4] dark:divide-primary/30">
                <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2.5 lg:py-1 xl:py-1.5 2xl:py-2 3xl:py-2.5 text-gray-500 font-medium truncate">
                  {param.label}
                </div>
                <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2.5 lg:py-1 xl:py-1.5 2xl:py-2 3xl:py-2.5 flex items-center justify-between font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221]">
                  {isSOPEditing && canEditParameters ? (
                    <div className="flex items-center gap-1 w-full">
                      <input
                        type="text"
                        value={param.value ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDraftParams((prev) =>
                            prev.map((p, idx) => (idx === pIdx ? { ...p, value: val } : p))
                          );
                        }}
                        className="w-full text-left px-1.5 py-0.5 rounded border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                      />
                      {param.unit && (
                        <span className="font-bold text-gray-900 dark:text-white text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs shrink-0">
                          {param.unit}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <span>{param.value || "-"}</span>
                      {param.unit && (
                        <span className="text-gray-500 dark:text-gray-400 font-normal text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
                          {param.unit}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Oven Temperature (Bakery only - hidden for Beverage, Beverage PSD, Confectionery) */}
        {shouldShowBakeSections && (
          <div>
            <div className="flex items-center gap-2 mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
              <span className="font-bold text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-900 dark:text-white">
                Oven Temperature
              </span>
              {isSOPEditing && !canEditOven && (
                <span className="text-[10px] lg:text-[7px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-medium">
                  Read-only
                </span>
              )}
            </div>

            {/* Tunnel Baking */}
            <div className="space-y-2 lg:space-y-1">
              <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-[#4B208B] dark:text-purple-300">
                Tunnel Baking
              </span>

              <div className="border border-[#EEEBF4] dark:border-primary/30 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] overflow-hidden bg-[#F9FAFB] dark:bg-[#121019] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs divide-y divide-[#EEEBF4] dark:divide-primary/30">
                <div className="flex items-center text-center font-bold text-gray-700 dark:text-gray-300 bg-[#FCFBFD] dark:bg-primary/15">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Zone</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30">1</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30">2</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30">3</div>
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Top Temperature</div>
                  {["zone1", "zone2", "zone3"].map((zk) => (
                    <div key={zk} className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                      {isSOPEditing && canEditOven ? (
                        <input
                          type="text"
                          value={topZone[zk] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftTunnelZones((prev) =>
                              prev.map((z) => (z.label === "Top Temperature" ? { ...z, [zk]: val } : z))
                            );
                          }}
                          className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                        />
                      ) : (
                        topZone[zk] ? `${topZone[zk]} °C` : "-"
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Bottom Temperature</div>
                  {["zone1", "zone2", "zone3"].map((zk) => (
                    <div key={zk} className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                      {isSOPEditing && canEditOven ? (
                        <input
                          type="text"
                          value={bottomZone[zk] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftTunnelZones((prev) =>
                              prev.map((z) => (z.label === "Bottom Temperature" ? { ...z, [zk]: val } : z))
                            );
                          }}
                          className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                        />
                      ) : (
                        bottomZone[zk] ? `${bottomZone[zk]} °C` : "-"
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Baking Time</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                    {isSOPEditing && canEditOven ? (
                      <input
                        type="text"
                        value={draftBakingTime ?? ""}
                        onChange={(e) => setDraftBakingTime(e.target.value)}
                        className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                      />
                    ) : (
                      draftBakingTime ? `${draftBakingTime} min` : "-"
                    )}
                  </div>
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Belt Speed</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                    {isSOPEditing && canEditOven ? (
                      <input
                        type="text"
                        value={draftBeltSpeed ?? ""}
                        onChange={(e) => setDraftBeltSpeed(e.target.value)}
                        className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                      />
                    ) : (
                      draftBeltSpeed ? `${draftBeltSpeed} min` : "-"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Rotary Baking (Image 3) */}
            <div className="space-y-2 lg:space-y-1 mt-4 lg:mt-2 xl:mt-2.5 2xl:mt-3 3xl:mt-4">
              <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-[#4B208B] dark:text-purple-300">
                Rotary Baking
              </span>

              <div className="border border-[#EEEBF4] dark:border-primary/30 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] overflow-hidden bg-[#F9FAFB] dark:bg-[#121019] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs divide-y divide-[#EEEBF4] dark:divide-primary/30">
                <div className="flex items-center text-center font-bold text-gray-700 dark:text-gray-300 bg-[#FCFBFD] dark:bg-primary/15">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Baking</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30">1st</div>
                  <div className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30">2nd</div>
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Rotary Temperature</div>
                  {["baking1", "baking2"].map((bk) => (
                    <div key={bk} className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                      {isSOPEditing && canEditOven ? (
                        <input
                          type="text"
                          value={ovenTempRow[bk] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftRotaryBakings((prev) =>
                              prev.map((b) => (b.label === "Rotary Temperature" || b.label === "Oven Temperature" ? { ...b, [bk]: val } : b))
                            );
                          }}
                          className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                        />
                      ) : (
                        ovenTempRow[bk] ? `${ovenTempRow[bk]} °C` : "-"
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Oven Time</div>
                  {["baking1", "baking2"].map((bk) => (
                    <div key={bk} className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                      {isSOPEditing && canEditOven ? (
                        <input
                          type="text"
                          value={ovenTimeRow[bk] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftRotaryBakings((prev) =>
                              prev.map((b) => (b.label === "Oven Time" ? { ...b, [bk]: val } : b))
                            );
                          }}
                          className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                        />
                      ) : (
                        ovenTimeRow[bk] ? `${ovenTimeRow[bk]} min` : "-"
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center text-center">
                  <div className="w-32 lg:w-18 xl:w-22 2xl:w-26 3xl:w-32 text-left pl-3 lg:pl-1.5 xl:pl-2 2xl:pl-2.5 3xl:pl-3 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 font-medium text-gray-500">Steam</div>
                  {["baking1", "baking2"].map((bk) => (
                    <div key={bk} className="flex-1 py-2 lg:py-1 xl:py-1.5 2xl:py-1.5 3xl:py-2 border-l border-[#EEEBF4] dark:border-primary/30 font-bold text-gray-900 dark:text-white px-1">
                      {isSOPEditing && canEditOven ? (
                        <input
                          type="text"
                          value={steamRow[bk] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftRotaryBakings((prev) =>
                              prev.map((b) => (b.label === "Steam" ? { ...b, [bk]: val } : b))
                            );
                          }}
                          className="w-full text-center px-1 py-0.5 rounded border border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221] outline-none"
                        />
                      ) : (
                        steamRow[bk] || "-"
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* After Bake: Analytical Report (Bakery only - hidden for Beverage, Beverage PSD, Confectionery) */}
        {shouldShowBakeSections && (
          <div>
            <div className="flex items-center gap-2 mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
              <span className="font-bold text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-900 dark:text-white">
                After Bake: Analytical Report
              </span>
              {isSOPEditing && isSamplePage && effectiveIsFromFinalRecipe && isBakery && (
                <span className="text-[10px] lg:text-[7px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 font-medium">
                  Editable
                </span>
              )}
            </div>

            <div className="border border-[#EEEBF4] dark:border-primary/30 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] overflow-hidden divide-y divide-[#EEEBF4] dark:divide-primary/30 bg-[#F9FAFB] dark:bg-[#121019] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">
              {draftAfterBake.map((param, pIdx) => (
                <div key={pIdx} className="flex items-center divide-x divide-[#EEEBF4] dark:divide-primary/30">
                  <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2.5 lg:py-1 xl:py-1.5 2xl:py-2 3xl:py-2.5 text-gray-500 font-medium truncate">
                    {param.label}
                  </div>
                  <div className="w-1/2 px-3.5 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-3.5 py-2.5 lg:py-1 xl:py-1.5 2xl:py-2 3xl:py-2.5 bg-white dark:bg-[#151221] font-bold text-gray-900 dark:text-white">
                    {isSOPEditing ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={param.value}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDraftAfterBake((prev) =>
                              prev.map((p) => (p.label === param.label ? { ...p, value: val } : p))
                            );
                          }}
                          className="w-full text-left px-1.5 py-0.5 rounded border-2 border-[#4B208B] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white bg-white dark:bg-[#151221]"
                        />
                        {param.unit && <span className="font-bold text-gray-900 dark:text-white text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs">{param.unit}</span>}
                      </div>
                    ) : (
                      <span className="">
                        {param.value ? `${param.value} ${param.unit || ""}`.trim() : "-"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Others Block */}
        {!hideOthers && (
          <div>
            <div className="mb-3 lg:mb-1.5 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
              <span className="font-bold text-sm lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-sm text-gray-900 dark:text-white">
                Others
              </span>
            </div>

            <div className="space-y-3 lg:space-y-1.5 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3">
              {Array.isArray(effectiveActivities) && effectiveActivities.length > 0 ? (
                effectiveActivities.map((act, actIdx) => (
                  <div
                    key={actIdx}
                    className="p-3.5 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-3.5 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] bg-[#F9FAFB] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30 space-y-2 lg:space-y-1 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs"
                  >
                    <div className="flex items-center gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                      <div className="w-7 lg:w-4 xl:w-5 2xl:w-6 3xl:w-7 h-7 lg:h-4 xl:h-5 2xl:h-6 3xl:h-7 rounded-full bg-primary text-white font-bold text-[13px] lg:text-[7px] xl:text-[9px] 2xl:text-[11px] 3xl:text-[13px] flex items-center justify-center">
                        {(act.userName || act.user || act.name || "U").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                        <span className="text-[14px] lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-[14px] font-semibold text-[#0D111A] dark:text-white truncate">
                          {act.userName || act.user || act.name || "User"}
                        </span>
                        {act.tag && act.tag !== "Application Recipe" && (
                          <span className="px-1.5 lg:px-1 py-0.5 rounded-md bg-[#EFEAF9] dark:bg-primary/25 text-[#4B208B] dark:text-purple-300 text-[10px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[10px] font-semibold whitespace-nowrap">
                            {act.tag}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] text-[#757575] whitespace-nowrap">
                        {(() => {
                          if (!act.createdAt) return "-";
                          if (typeof formatDate === "function") {
                            const res = formatDate(act.createdAt);
                            if (res && res !== act.createdAt) return res;
                          }
                          const dObj = new Date(act.createdAt);
                          if (isNaN(dObj.getTime())) return String(act.createdAt);
                          return dObj.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });
                        })()}
                      </span>
                    </div>
                    <p className="text-[#0D111A] dark:text-gray-300 leading-relaxed text-[14px] lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-[14px] font-medium">
                      {act.text || act.comment || ""}
                    </p>
                  </div>
                ))
              ) : null}

              {/* Comment Input Box */}
              <div className="space-y-2 lg:space-y-1">
                <textarea
                  rows={3}
                  placeholder="Enter Comment"
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] border border-[#EEEBF4] dark:border-primary/30 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-[#FCFBFD] dark:bg-[#121019] text-gray-900 dark:text-white resize-none focus:outline-none"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSendComment}
                    disabled={!newCommentText.trim()}
                    className="px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-2 lg:py-1 xl:py-1 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] bg-primary hover:bg-[#3E1B77] text-white font-semibold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs flex items-center gap-1 shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ================= STANDALONE SOP OTHERS COMPONENT =================
export function StandardOperatingProcedureOthers({
  vItem,
  data,
  normalizedVItem,
  formatDate = (d) => d || "-",
  onSaveSpecificFields,
  vIsFinalized = false,
  className,
}) {
  const [newCommentText, setNewCommentText] = useState("");
  const [localActivities, setLocalActivities] = useState([]);
  const { user: currentUser } = useAuthStore();
  const currentUserName = currentUser?.name || currentUser?.fullName || currentUser?.username || "User";

  const isApproved =
    vIsFinalized ||
    ["final", "approved", "finalized"].includes(
      String(
        vItem?.recipeStatus ||
          vItem?.status ||
          normalizedVItem?.recipeStatus ||
          normalizedVItem?.status ||
          data?.recipeStatus ||
          data?.status ||
          ""
      ).toLowerCase()
    );

  const dynamicAuthor =
    vItem?.createdBy?.name ||
    vItem?.createdBy?.fullName ||
    (typeof vItem?.createdBy === "string" && vItem.createdBy.length < 30 ? vItem.createdBy : null) ||
    data?.createdBy?.name ||
    data?.createdBy?.fullName ||
    data?.independentRecipeRaisedBy ||
    data?.raisedBy ||
    currentUserName;

  const storedActivities = useMemo(() => {
    const list = [];
    if (vItem) {
      if (Array.isArray(vItem.activities) && vItem.activities.length > 0) {
        list.push(...vItem.activities);
      }
    } else if (Array.isArray(data?.activities) && data.activities.length > 0) {
      list.push(...data.activities);
    }

    const procOthers = vItem
      ? (vItem.procedureOthers || normalizedVItem?.procedureOthers)
      : data?.procedureOthers;

    if (procOthers && (typeof procOthers === "string" || Array.isArray(procOthers))) {
      const parsed = parseStoredComments(
        procOthers,
        dynamicAuthor,
        vItem?.updatedAt || vItem?.createdAt || data?.updatedAt || data?.createdAt
      );

      parsed.forEach((p) => {
        const textKey = (p.text || p.comment || "").trim();
        const dateKey = p.createdAt ? new Date(p.createdAt).getTime() : 0;
        const exists = list.some((existing) => {
          const eText = (existing.text || existing.comment || "").trim();
          const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
          return eText === textKey && (Math.abs(eDate - dateKey) < 1000 || !eDate || !dateKey);
        });
        if (!exists) {
          list.push(p);
        }
      });
    }

    return list;
  }, [vItem, normalizedVItem, data, dynamicAuthor]);

  const effectiveActivities = useMemo(() => {
    const list = [...storedActivities];
    localActivities.forEach((local) => {
      const lText = (local.text || local.comment || "").trim();
      const lDate = local.createdAt ? new Date(local.createdAt).getTime() : 0;
      const exists = list.some((existing) => {
        const eText = (existing.text || existing.comment || "").trim();
        const eDate = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
        return eText === lText && (Math.abs(eDate - lDate) < 1000 || !eDate || !lDate);
      });
      if (!exists) {
        list.push(local);
      }
    });
    return list;
  }, [storedActivities, localActivities]);

  const handleSendComment = async () => {
    const text = newCommentText.trim();
    if (!text) return;

    try {
      const targetId = vItem?._id || data?._id;
      const newEntry = {
        userName: currentUserName,
        user: currentUserName,
        name: currentUserName,
        text: text,
        comment: text,
        createdAt: new Date().toISOString(),
      };
      const updatedList = [...effectiveActivities, newEntry];
      setLocalActivities((prev) => [...prev, newEntry]);
      setNewCommentText("");

      if (onSaveSpecificFields) {
        await onSaveSpecificFields({ procedureOthers: JSON.stringify(updatedList) }, targetId);
      }
    } catch (err) {
      console.error("Failed to post comment:", err);
    }
  };

  return (
    <div className={cn("border border-[#EEEBF4] dark:border-primary/40 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl overflow-hidden shadow-xs bg-white dark:bg-[#0D0B14]", className)}>
      <div className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 bg-[#FAF8FD] dark:bg-primary/10 border-b border-[#EEEBF4] dark:border-primary/30">
        <h3 className="text-base lg:text-[9.5px] xl:text-xs 2xl:text-sm 3xl:text-base font-bold text-gray-900 dark:text-white">
          Others
        </h3>
      </div>
      <div className="p-6 lg:p-3 xl:p-4 2xl:p-5 3xl:p-6 space-y-4 lg:space-y-2 xl:space-y-2.5 2xl:space-y-3 3xl:space-y-4">
        <div className="space-y-3 lg:space-y-1.5 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3">
          {Array.isArray(effectiveActivities) && effectiveActivities.length > 0 ? (
            effectiveActivities.map((act, actIdx) => (
              <div
                key={actIdx}
                className="p-3.5 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-3.5 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] bg-[#F9FAFB] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30 space-y-2 lg:space-y-1 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs"
              >
                <div className="flex items-center gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                  <div className="w-7 lg:w-4 xl:w-5 2xl:w-6 3xl:w-7 h-7 lg:h-4 xl:h-5 2xl:h-6 3xl:h-7 rounded-full bg-primary text-white font-bold text-[13px] lg:text-[7px] xl:text-[9px] 2xl:text-[11px] 3xl:text-[13px] flex items-center justify-center">
                    {(act.userName || act.user || act.name || "U").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                    <span className="text-[14px] lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-[14px] font-semibold text-[#0D111A] dark:text-white truncate">
                      {act.userName || act.user || act.name || "User"}
                    </span>
                    {act.tag && act.tag !== "Application Recipe" && (
                      <span className="px-1.5 lg:px-1 py-0.5 rounded-md bg-[#EFEAF9] dark:bg-primary/25 text-[#4B208B] dark:text-purple-300 text-[10px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[10px] font-semibold whitespace-nowrap">
                        {act.tag}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] text-[#757575] whitespace-nowrap">
                    {(() => {
                      if (!act.createdAt) return "-";
                      if (typeof formatDate === "function") {
                        const res = formatDate(act.createdAt);
                        if (res && res !== act.createdAt) return res;
                      }
                      const dObj = new Date(act.createdAt);
                      if (isNaN(dObj.getTime())) return String(act.createdAt);
                      return dObj.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      });
                    })()}
                  </span>
                </div>
                <p className="text-[#0D111A] dark:text-gray-300 leading-relaxed text-[14px] lg:text-[8px] xl:text-[10px] 2xl:text-xs 3xl:text-[14px] font-medium">
                  {act.text || act.comment || ""}
                </p>
              </div>
            ))
          ) : (
            <div className="p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 text-center border border-dashed border-[#EEEBF4] dark:border-primary/30 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-400 dark:text-gray-500">
              No comments recorded under Others.
            </div>
          )}

          {/* Comment Input Box */}
          <div className="space-y-2 lg:space-y-1 pt-2 lg:pt-1">
            <textarea
              rows={3}
              placeholder="Enter Comment"
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              className="w-full p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-[8px] lg:rounded-[4px] xl:rounded-[6px] 2xl:rounded-[6px] 3xl:rounded-[8px] border border-[#EEEBF4] dark:border-primary/30 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs leading-relaxed bg-[#FCFBFD] dark:bg-[#121019] text-gray-900 dark:text-white resize-none focus:outline-none"
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSendComment}
                disabled={!newCommentText.trim()}
                className="px-4 lg:px-2 xl:px-2.5 2xl:px-3 3xl:px-4 py-2 lg:py-1 xl:py-1 2xl:py-1.5 3xl:py-2 rounded-[6px] lg:rounded-[4px] bg-primary hover:bg-[#3E1B77] text-white font-semibold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs flex items-center gap-1 shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================= STANDARD OPERATING PROCEDURE LEFT COLUMN =================
export function StandardOperatingProcedureLeftHeader({ height, className }) {
  return (
    <div
      style={height ? { height: `${height}px` } : undefined}
      className={cn("px-6 lg:px-3 xl:px-4 2xl:px-5 3xl:px-6 py-4 lg:py-2 xl:py-2.5 2xl:py-3 3xl:py-4 border-b border-[#EEEBF4] dark:border-primary/40 flex flex-col justify-start", className)}
    >
      <h2 className="text-[20px] lg:text-[12px] xl:text-[15px] 2xl:text-[18px] 3xl:text-[20px] font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
        Standard Operating<br />Procedure
      </h2>
    </div>
  );
}
